import { useState, useEffect } from 'react';
import { getMissionPage, type MissionPage } from '@/lib/firebase/mission';
import { getUserData, type UserData } from '@/lib/firebase/user';
import { toast } from 'sonner';

export function useMissionPage(userId: string | undefined) {
  const [missionPage, setMissionPage] = useState<MissionPage | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      try {
        // allSettled, NOT all. getUserData() throws, and users/{uid} is
        // `allow get: if isSignedIn()` -- so a token that has not finished
        // attaching right after sign-in rejects that read, and with Promise.all
        // the mission page was thrown away with it. missionPage stayed null,
        // the dashboard announced "your page is not published", and the next
        // save wrote that null back over a live page.
        const [pageResult, userResult] = await Promise.allSettled([
          getMissionPage(userId),
          getUserData(userId)
        ]);

        if (pageResult.status === 'fulfilled') setMissionPage(pageResult.value);
        if (userResult.status === 'fulfilled') setUserData(userResult.value);

        if (pageResult.status === 'rejected' && userResult.status === 'rejected') {
          const msg = (pageResult.reason as any)?.message || 'Failed to load fund page';
          setError(msg);
          toast.error(msg);
        } else if (pageResult.status === 'rejected') {
          // The page itself is what the publish state is read from; surface it
          // rather than letting the UI infer "unpublished" from a failed read.
          setError((pageResult.reason as any)?.message || 'Failed to load fund page');
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to load fund page';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [userId]);

  return {
    missionPage,
    userData,
    isLoading,
    error,
    setMissionPage,
    setUserData
  };
}